import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Box, Typography, Chip, LinearProgress, Grid, IconButton, Menu, MenuItem } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import captureScreenshot from 'utils/screenshotUtils';
import { motion, useAnimation, useInView } from 'framer-motion';
import PropTypes from 'prop-types';
// import MainCard from 'components/MainCard';

// Animation component similar to what's used in interviews section
const Animation = ({ children, variants }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      animate={controls}
      initial="hidden"
      transition={{
        x: { type: 'spring', stiffness: 150, damping: 30, duration: 0.5 },
        opacity: { duration: 1 }
      }}
      variants={variants}
    >
      {children}
    </motion.div>
  );
};

Animation.propTypes = {
  children: PropTypes.node,
  variants: PropTypes.object
};

const CompetitorSection = ({ competitor, name, maxMentions }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{
      type: 'spring',
      stiffness: 100,
      damping: 15,
      duration: 0.6
    }}
    whileHover={{
      y: -5,
      boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
      transition: { duration: 0.3 }
    }}
  >
    <Card
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardContent sx={{ p: 3, flexGrow: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {competitor.mentions} mentions
              </Typography>
            </Box>
            <Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Threat Level
                </Typography>
                <Chip
                  label={competitor.competitive_threat_level}
                  color={
                    competitor.competitive_threat_level?.toLowerCase() === 'high'
                      ? 'error'
                      : competitor.competitive_threat_level?.toLowerCase() === 'medium'
                      ? 'warning'
                      : 'info'
                  }
                  sx={{ opacity: 0.8 }}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: '100%', mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={(competitor.mentions / maxMentions) * 100}
              sx={{
                height: 8,
                borderRadius: 5,
                backgroundColor: 'secondary.lighter',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  backgroundColor: 'primary.main'
                }
              }}
            />
          </Box>

          {competitor.switching_reasons && competitor.switching_reasons.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Switching Reasons:
              </Typography>
              <Box
                sx={{
                  ml: 1,
                  pl: 2,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  py: 1
                }}
              >
                {competitor.switching_reasons.map((reason, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {reason}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Display comparative advantages and disadvantages in a 2-column layout */}
          {(competitor.comparative_advantages?.length > 0 || competitor.comparative_disadvantages?.length > 0) && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                {/* Their Advantages Column */}
                {competitor.comparative_advantages && competitor.comparative_advantages.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Their Advantages:
                    </Typography>
                    <Box
                      sx={{
                        ml: 1,
                        pl: 2,
                        borderLeft: '3px solid',
                        borderColor: 'warning.main',
                        py: 1
                      }}
                    >
                      {competitor.comparative_advantages.map((advantage, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                          • {advantage}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Our Advantages Column */}
                {competitor.comparative_disadvantages && competitor.comparative_disadvantages.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Our Advantages:
                    </Typography>
                    <Box
                      sx={{
                        ml: 1,
                        pl: 2,
                        borderLeft: '3px solid',
                        borderColor: 'success.main',
                        py: 1
                      }}
                    >
                      {competitor.comparative_disadvantages.map((disadvantage, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                          • {disadvantage}
                        </Typography>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

// Function to convert competitor data to RTF format for DOC download
const convertToRTF = (competitors) => {
  let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

  // Escape RTF characters
  const escapeRTF = (text) => {
    if (typeof text !== 'string') {
      text = JSON.stringify(text) || '';
    }
    return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
  };

  // Add title
  rtf += '\\b\\fs28 Competitor Analysis\\b0\\fs20\\par\\par\n';

  // Add competitors
  Object.entries(competitors).forEach(([name, competitor]) => {
    rtf += `\\b\\fs24 ${escapeRTF(name)}\\b0\\fs20 (${competitor.mentions} mentions)\\par\n`;
    rtf += `Threat Level: ${escapeRTF(competitor.competitive_threat_level)}\\par\\par\n`;

    // Switching reasons
    if (competitor.switching_reasons && competitor.switching_reasons.length > 0) {
      rtf += '\\b Switching Reasons:\\b0\\par\n';
      competitor.switching_reasons.forEach((reason) => {
        rtf += `• ${escapeRTF(reason)}\\par\n`;
      });
      rtf += '\\par\n';
    }

    // Their advantages
    if (competitor.comparative_advantages && competitor.comparative_advantages.length > 0) {
      rtf += '\\b Their Advantages:\\b0\\par\n';
      competitor.comparative_advantages.forEach((advantage) => {
        rtf += `• ${escapeRTF(advantage)}\\par\n`;
      });
      rtf += '\\par\n';
    }

    // Our advantages
    if (competitor.comparative_disadvantages && competitor.comparative_disadvantages.length > 0) {
      rtf += '\\b Our Advantages:\\b0\\par\n';
      competitor.comparative_disadvantages.forEach((disadvantage) => {
        rtf += `• ${escapeRTF(disadvantage)}\\par\n`;
      });
      rtf += '\\par\n';
    }

    rtf += '\\par\n';
  });

  rtf += '}';
  return rtf;
};

const CompetitiveAnalysis = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);
  const competitors = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.competitive_analysis);

  if (!competitors) return null;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'competitor-analysis',
      afterCapture: handleClose
    });
  };

  const downloadAsDoc = () => {
    const rtfContent = convertToRTF(competitors);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'competitor-analysis.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  // Find max mentions for percentage calculation
  const maxMentions = Math.max(...Object.values(competitors).map((c) => c.mentions));

  // Calculate content size for each competitor to determine card height
  const getContentSize = (competitor) => {
    let size = 0;

    // Base size for name and mentions
    size += 1;

    // Add switching reasons count
    if (competitor.switching_reasons && competitor.switching_reasons.length > 0) {
      size += 1 + competitor.switching_reasons.length;
    }

    // Add comparative advantages count
    if (competitor.comparative_advantages && competitor.comparative_advantages.length > 0) {
      size += 1 + competitor.comparative_advantages.length;
    }

    // Add comparative disadvantages count
    if (competitor.comparative_disadvantages && competitor.comparative_disadvantages.length > 0) {
      size += 1 + competitor.comparative_disadvantages.length;
    }

    return size;
  };

  // Sort competitors by content size (descending)
  const sortedCompetitors = Object.entries(competitors).sort(([, a], [, b]) => {
    return getContentSize(b) - getContentSize(a);
  });

  // Distribute competitors to columns using a greedy algorithm to balance content
  const leftColumnCompetitors = [];
  const rightColumnCompetitors = [];

  // Track total content size in each column
  let leftColumnContentSize = 0;
  let rightColumnContentSize = 0;

  // Add each competitor to the column with less total content
  sortedCompetitors.forEach((competitor) => {
    const competitorSize = getContentSize(competitor[1]);

    if (leftColumnContentSize <= rightColumnContentSize) {
      leftColumnCompetitors.push(competitor);
      leftColumnContentSize += competitorSize;
    } else {
      rightColumnCompetitors.push(competitor);
      rightColumnContentSize += competitorSize;
    }
  });

  return (
    <Card sx={{ width: '100%', mt: 3 }} ref={contentRef}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              Competitor Mentions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Competitor mentions and comparative analysis
            </Typography>
          </Box>

          <Box className="screenshot-controls">
            <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
              <DownloadIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }}
            >
              <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
              <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Grid container spacing={6} sx={{ mt: 1 }}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {leftColumnCompetitors.map(([name, competitor]) => (
                <Grid item xs={12} key={name}>
                  <CompetitorSection competitor={competitor} name={name} maxMentions={maxMentions} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {rightColumnCompetitors.map(([name, competitor]) => (
                <Grid item xs={12} key={name}>
                  <CompetitorSection competitor={competitor} name={name} maxMentions={maxMentions} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CompetitiveAnalysis;
